/**
 * What the operator is told about a call, and why each outcome is named separately.
 *
 * Pure, so the wording is testable without a browser. Every failure reason maps to its own sentence
 * because they call for different actions: grant the microphone, wait for the helmet to reconnect,
 * or stop trying because this firmware does not answer calls. One generic "call failed" would hide
 * that distinction at the exact moment it matters most.
 */

import type { HelmetCallPhase } from './call-resources'

/** Failure reason → i18n key suffix + the English fallback that ships with it. */
export const CALL_FAILURE_COPY: Record<string, string> = {
  // The device was told to join and never did. This is the one outcome the firmware gives no signal
  // for, so the broker's probe is what produces it.
  unanswered: 'The helmet did not answer. It may not support calls.',
  mic_denied: 'Microphone access was blocked. Allow it and try again.',
  mic_failed: 'Your microphone could not be opened.',
  mic_unavailable: 'No microphone is available.',
  // The handshake hangs off a broker-synthesised ack, and there is no reply at all when the broker
  // has no media configuration, or when the command was published before the socket opened. Named
  // rather than folded into `failed`, because the actions differ: this one is ours to fix.
  no_broker_ack: 'The call was never acknowledged by the server. It may not be set up for calls.',
  offline: 'This helmet is not connected.',
  channel_lost: 'Lost the connection to the helmet.',
  connection_lost: 'The call dropped.',
  publish_failed: 'Your microphone could not reach the call.',
  media_failed: "Could not receive the wearer's audio.",
  // Distinct from media_failed on purpose: nothing errored, the feed simply never arrived — which
  // on this deployment most often means the media server could not transcode it.
  media_timeout: "The wearer's audio never arrived. They may not be able to hear you either.",
  call_gone: 'The call has already ended.',
  calls_unavailable: 'Calling is not available on this deployment.',
  // The broker is running but carries no call configuration — the three media SSM parameters
  // are absent, so there is no media server to carry the audio. Named separately from
  // `rejected` because it is the ONLY failure here that no operator can do anything about, and
  // the generic "The call was refused" sends them hunting for a permission or a device fault.
  calls_not_configured: 'Calling is not set up on this deployment yet. Contact your administrator.',
  // The helmet's connection is up but it has not sent `ca_login` yet, so the broker cannot put
  // it in a member list keyed on its IMEI. Transient, and retrying is genuinely the fix.
  device_unidentified: 'The helmet has not finished connecting. Try again in a moment.',
  // `call_start` reached the broker with no prepared call behind it — the session was reaped
  // mid-handshake, or this operator is no longer one of its viewers.
  not_prepared: 'The call was not set up. Try again.',
  // The one a real operator meets: a read-only account may watch a helmet but not command it.
  forbidden_role: 'Your account cannot place calls. Ask an administrator.',
  // Any other refusal the broker sends. The code is shown as the detail rather than hidden, because
  // an unexplained refusal on an emergency screen is worse than an unfamiliar word.
  rejected: 'The call was refused.',
  failed: 'The call could not be placed.',
}

/** Ended reason → English fallback. `hung_up` is the operator's own action and needs no excuse. */
export const CALL_ENDED_COPY: Record<string, string> = {
  hung_up: 'Call ended.',
  idle: 'The call timed out.',
  max: 'The call reached its time limit.',
}

export interface CallCopy {
  /** i18n key under `alerts.sos.call`. */
  key: string
  fallback: string
  /** Whether this is a resting state the operator should be able to dismiss. */
  terminal: boolean
}

/** One line describing where the call is. */
export function callStatusCopy(phase: HelmetCallPhase): CallCopy {
  switch (phase.kind) {
    case 'starting':
      return { key: 'starting', fallback: 'Getting your microphone ready…', terminal: false }
    case 'ringing':
      // Deliberately not "connecting": the wearer's helmet is being asked to join, and on a device
      // in a pocket that can take several seconds. Saying "ringing" sets the right expectation.
      return { key: 'ringing', fallback: 'Ringing the helmet…', terminal: false }
    case 'connected':
      return { key: 'connected', fallback: 'Connected', terminal: false }
    case 'ended':
      return {
        key: `ended.${phase.reason ?? 'hung_up'}`,
        fallback: CALL_ENDED_COPY[phase.reason ?? 'hung_up'] ?? 'Call ended.',
        terminal: true,
      }
    case 'failed':
      return {
        key: `failed.${phase.reason}`,
        fallback: CALL_FAILURE_COPY[phase.reason] ?? CALL_FAILURE_COPY.failed!,
        terminal: true,
      }
    default:
      return { key: 'idle', fallback: '', terminal: true }
  }
}

/** Whether the call is live enough that hanging up is the meaningful action. */
export function callIsActive(phase: HelmetCallPhase): boolean {
  return phase.kind === 'starting' || phase.kind === 'ringing' || phase.kind === 'connected'
}
